import React from "react";

import { useIntl } from "react-intl";

import { BackLink } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import { meSelector } from "Reducers/app/selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { SubscribeCtaBox } from "src/organization/common/components/SubscribeCtaBox/SubscribeCtaBox";
import { useAppSelector } from "Store/hooks";

import * as O from "../BillingOverview.styles";
import MonthlyEstimate from "../MonthlyEstimate";

import * as S from "./Aside.style";

type AsideProps = {
  page: "organization" | "project";
  totalCurrentMonth?: string;
  totalNextMonth?: string;
  currentSpend?: string;
  children?: React.ReactNode;
  isTrial: boolean;
};

const Aside = ({
  page,
  totalCurrentMonth,
  totalNextMonth,
  currentSpend,
  children,
  isTrial
}: AsideProps) => {
  const intl = useIntl();
  const { organizationId } = useDecodedParams<{
    organizationId: string | undefined;
  }>();

  const me = useAppSelector(meSelector);
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const canEditBilling =
    organization?.owner_id === me?.id || organization?.hasLink("address");

  if (!organizationId) return null;

  return (
    <S.Aside>
      <O.InnerWrapper>
        {page === "project" && (
          <BackLink
            to={getUrl({
              key: "organization.billing",
              props: { organizationId }
            })}
            from=""
          >
            {intl.formatMessage({ id: "back", defaultMessage: "Back" })}
          </BackLink>
        )}

        <O.Stack space={32}>
          <O.Stack>
            <S.BillingTitle tag="h2">
              {intl.formatMessage({
                id: `${page}.billing.title`
              })}
            </S.BillingTitle>

            {totalCurrentMonth && (
              <MonthlyEstimate
                analyticId="organization.billing.projected_total.description.current"
                title={intl.formatMessage({
                  id: "organization.billing.this_month"
                })}
                label={intl.formatMessage({
                  id: "organization.billing.monthly_estimate.current"
                })}
                tooltipTitle={intl.formatMessage({
                  id: "organization.billing.projected_total.title.current"
                })}
                tooltipDescription={intl.formatMessage({
                  id: "organization.billing.projected_total.description.current"
                })}
                total={totalCurrentMonth}
                currentSpend={currentSpend}
                description={
                  isTrial
                    ? intl.formatMessage({
                        id: "organization.billing.trial_discount_not_included"
                      })
                    : undefined
                }
              />
            )}

            {totalNextMonth && (
              <MonthlyEstimate
                variant="dark"
                analyticId="organization.billing.projected_total.description.future"
                title={intl.formatMessage({
                  id: "organization.billing.future_months"
                })}
                label={intl.formatMessage({
                  id: "organization.billing.monthly_estimate.next"
                })}
                tooltipTitle={intl.formatMessage({
                  id: "organization.billing.projected_total.title.future"
                })}
                tooltipDescription={intl.formatMessage({
                  id: "organization.billing.projected_total.description.future"
                })}
                total={totalNextMonth}
              />
            )}
          </O.Stack>

          {isTrial && (
            <SubscribeCtaBox
              organizationId={organizationId}
              canSubscribe={canEditBilling}
            />
          )}

          {children}
        </O.Stack>
      </O.InnerWrapper>
    </S.Aside>
  );
};

export default Aside;
